import React from 'react';
import { MapPin, Phone, ArrowUpRight } from 'lucide-react';
import { brand } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import { useAsset } from '../assets/AssetsContext';

const locationImages = {
  tr: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=900&q=75',
  ma: 'https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=900&q=75',
};

const LocationCard = ({ loc, t }) => {
  const img = useAsset(`loc.${loc.id}`, locationImages[loc.id]);
  return (
    <article className="group relative rounded-3xl overflow-hidden bg-white card-hover shadow-xl shadow-[#0B3D2E]/5">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={img}
          alt={`Gold Care Esthetic ${loc.city}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D2E]/75 via-[#0B3D2E]/10 to-transparent" />
        <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 backdrop-blur text-xs font-semibold text-[#0B3D2E]">
          <span className="text-base">{loc.flag}</span>
          {loc.country}
        </div>
        <div className="absolute bottom-5 left-5 right-5">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] mb-1">
            {t(`loc.${loc.id}.tagline`, loc.tagline)}
          </div>
          <h3 className="font-serif-display text-3xl md:text-4xl text-white">
            {loc.city}
          </h3>
        </div>
      </div>

      <div className="p-7">
        <div className="flex items-start gap-3 mb-4">
          <MapPin className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
          <p className="text-sm text-[#0B3D2E]/80 leading-relaxed">{t(`loc.${loc.id}.addr`, loc.address)}</p>
        </div>
        <div className="flex items-start gap-3 mb-6">
          <Phone className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
          <a
            href={`tel:${loc.tel}`}
            className="text-sm font-medium text-[#0B3D2E] hover:text-[#D4AF37] transition-colors"
          >
            {loc.phone}
          </a>
        </div>

        <a
          href={loc.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#0B3D2E] text-[#ffffff] text-sm font-medium hover:bg-[#175C45] transition-colors"
        >
          {t('loc.visit')}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </article>
  );
};

const Locations = () => {
  const { t } = useLang();
  return (
    <section id="locations" className="py-24 md:py-32 bg-[#f5ede2] text-[#0B3D2E]">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#0B3D2E]/20 text-[#0B3D2E]/70 text-xs tracking-[0.22em] uppercase mb-6">
            {t('loc.kicker')}
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-4">
            {t('loc.title')} <span className="peach-text italic">{t('loc.title.italic')}</span>
          </h2>
          <p className="text-[#0B3D2E]/65">
            {t('loc.sub')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {brand.locations.map((loc) => (
            <LocationCard key={loc.id} loc={loc} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;
